import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /** @type {Map<number, string>} */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  // --- CUSTOM CACHE BYPASS VARIABLES ---
  #storageKey;
  #pendingRestoreCardId = null;
  #pendingRestoreScroll = null;
  #restoreQueued = false;
  #isRestoring = false;
  #isAppending = false;
  #navType = 'navigate';
  #skipTransitionWait = false;

  connectedCallback() {
    if (this.hasAttribute('data-paginated-initialized')) return;
    this.setAttribute('data-paginated-initialized', '');

    super.connectedCallback();

    // ALLOW NATIVE BROWSER SNAPSHOT: Changed from 'manual' to 'auto'
    if ('scrollRestoration' in history) history.scrollRestoration = 'auto';

    this.#navType = this.#getNavType();
    this.#skipTransitionWait = this.#navType === 'back_forward';

    // Instantly reveal the page and kill white screen
    this.#fastRevealPage();

    document.documentElement.removeAttribute('scroll-lock');
    document.body.removeAttribute('scroll-lock');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';

    this.#storageKey = `cp-plp-fastsnap:${window.location.pathname}:${this.sectionId}`;

    const didRestore = this.#restoreSnapshotAndQueueRehydrate();

    const runHeavyInit = () => {
      /** @type {HTMLElement | null} */
      const templateCard = this.querySelector('[ref="cardGallery"]');
      if (templateCard) {
        this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });
      }
      
      this.#aspectRatioHelper?.processNewElements?.();
      
      // Setup original fetches and observers
      this.#fetchPage('next');
      this.#fetchPage('previous');
      this.#observeViewMore();

      // Setup custom button bindings if they exist on the page
      this.#bindPaginationButtons();
      this.#updatePaginationText();

      document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    };

    if (didRestore) {
      this.#isRestoring = true;
      runHeavyInit();
      // Inject pages and THEN hide the curtain (Fallback for when BFCache fails)
      this.#rehydrateExpandedPagesInstant();
    } else {
      runHeavyInit();
      // Normal load: Hide the curtain immediately
      this.#hideLoader();
    }

    // Save snapshot when clicking ANY part of a product card (Fallback data)
    this.addEventListener(
      'click',
      (e) => {
        const card = e.target.closest('.product-grid__item');
        const isPlpButton = e.target.closest('.custom-plp-buttons');

        if (card && !isPlpButton) {
          const cardId = card.id || null;
          this.#saveSnapshot(cardId);
        }
      },
      true
    );

    window.addEventListener('pageshow', (e) => {
      this.#fastRevealPage();
      this.#isAppending = false;
      this.#resetAllButtonStates();

      // IF BROWSER NATIVELY RESTORED SNAPSHOT (BFCache):
      if (e.persisted) {
        // Do NOT manually scroll. Let the browser handle the visual snapshot.
        // Just clean up our session storage flags and hide the loader.
        sessionStorage.removeItem(this.#storageKey + ':returning');
        this.#hideLoader();
        return;
      }

      // If BFCache failed and forced a hard reload, use the manual fallback
      const navType = this.#getNavType();
      if (navType === 'back_forward') {
        const savedScroll = sessionStorage.getItem(this.#storageKey + ':scroll');
        const savedCardId = sessionStorage.getItem(this.#storageKey + ':cardId');

        this.#pendingRestoreScroll = savedScroll ? parseInt(savedScroll, 10) || 0 : null;
        this.#pendingRestoreCardId = savedCardId || null;

        // Restore scroll instantly behind the 3-dot curtain, then fade it out
        setTimeout(() => {
          this.#attemptRestoreScroll(false);
          this.#hideLoader();
        }, 50);
      } else {
        this.#hideLoader();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeAttribute('data-paginated-initialized');
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /* ----------------------------------------------------
     CURTAIN LOADER LOGIC
  ---------------------------------------------------- */
  #hideLoader() {
    const loader = document.getElementById('cp-smart-loader');
    if (loader) {
      loader.style.opacity = '0';
      setTimeout(() => {
        if (loader.parentNode) loader.remove();
      }, 400); 
    }
  }

  /* ----------------------------------------------------
     NAV + PAINT HELPERS
  ---------------------------------------------------- */
  #getNavType() {
    try {
      const navEntries = performance.getEntriesByType?.('navigation');
      const nav = navEntries && navEntries[0];
      const t = nav?.type;
      if (t) return t; 
    } catch (e) {}
    return 'navigate';
  }

  #fastRevealPage() {
    try {
      const blocker = document.getElementById('view-transition-render-blocker');
      if (blocker) blocker.remove();

      document.documentElement.removeAttribute('inert');
      document.documentElement.classList.remove(
        'is-transitioning', 'page-transitioning', 'view-transition', 
        'view-transitioning', 'is-loading', 'loading'
      );
      document.body.classList.remove('is-transitioning', 'page-transitioning', 'is-loading', 'loading');

      if (document.documentElement.style.opacity === '0') document.documentElement.style.opacity = '';
      if (document.body.style.opacity === '0') document.body.style.opacity = '';
      if (document.documentElement.style.visibility === 'hidden') document.documentElement.style.visibility = '';
      if (document.body.style.visibility === 'hidden') document.body.style.visibility = '';
      if (document.body.style.display === 'none') document.body.style.display = '';
    } catch (e) {}
  }

  /* ----------------------------------------------------
     SNAPSHOT SAVE & REHYDRATE (Kept as fallback)
  ---------------------------------------------------- */
  #saveSnapshot(cardId = null) {
    try {
      sessionStorage.setItem(this.#storageKey + ':scroll', String(window.scrollY || 0));
      if (cardId) sessionStorage.setItem(this.#storageKey + ':cardId', cardId);

      const cards = Array.from(this.querySelectorAll('[ref="cards[]"]'));
      let maxPage = 1;
      for (const el of cards) {
        const p = Number(el?.dataset?.page || 1);
        if (p > maxPage) maxPage = p;
      }
      sessionStorage.setItem(this.#storageKey + ':maxPage', String(maxPage));
      sessionStorage.setItem(this.#storageKey + ':returning', '1');
    } catch (err) {}
  }

  #restoreSnapshotAndQueueRehydrate() {
    try {
      const isReturning = sessionStorage.getItem(this.#storageKey + ':returning') === '1';
      if (!isReturning) return false;

      const savedScroll = sessionStorage.getItem(this.#storageKey + ':scroll');
      const savedCardId = sessionStorage.getItem(this.#storageKey + ':cardId');

      this.#pendingRestoreScroll = savedScroll ? parseInt(savedScroll, 10) || 0 : null;
      this.#pendingRestoreCardId = savedCardId || null;

      sessionStorage.removeItem(this.#storageKey + ':returning');
      return true;
    } catch (err) {}
    return false;
  }

  #rehydrateExpandedPagesInstant() {
    if (this.#restoreQueued) return;
    this.#restoreQueued = true;

    const { grid } = this.refs;
    if (!grid) {
      this.#attemptRestoreScroll(true);
      this.#hideLoader();
      this.#finishRestoreMode();
      return;
    }

    const maxPage = Number(sessionStorage.getItem(this.#storageKey + ':maxPage') || '1');
    if (maxPage <= 1) {
      this.#attemptRestoreScroll(true);
      this.#hideLoader();
      this.#finishRestoreMode();
      return;
    }

    const existingCards = Array.from(this.querySelectorAll('[ref="cards[]"]'));
    let existingMaxPage = 1;
    for (const el of existingCards) {
      const p = Number(el?.dataset?.page || 1);
      if (p > existingMaxPage) existingMaxPage = p;
    }

    const startPage = Math.max(2, existingMaxPage + 1);
    let allHtml = '';

    for (let p = startPage; p <= maxPage; p++) {
      const html = sessionStorage.getItem(this.#storageKey + `:page:${p}`);
      if (html) {
        allHtml += html;
      } else {
        break; 
      }
    }

    if (!allHtml) {
      this.#attemptRestoreScroll(true);
      this.#hideLoader();
      this.#finishRestoreMode();
      return;
    }

    grid.insertAdjacentHTML('beforeend', allHtml);

    if (this.#aspectRatioHelper) {
      this.#aspectRatioHelper.processNewElements();
    }
    this.#updatePaginationText();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#attemptRestoreScroll(true);
        this.#hideLoader();
        this.#finishRestoreMode();
      });
    });
  }

  #finishRestoreMode() {
    this.#isRestoring = false;
    requestIdleCallback(() => this.#fetchPage('next'));
  }

  #attemptRestoreScroll(force = false) {
    try {
      if (this.#pendingRestoreCardId) {
        const card = document.getElementById(this.#pendingRestoreCardId);
        if (card) {
          card.scrollIntoView({ behavior: 'instant', block: 'center' });
          if (force) {
            this.#pendingRestoreCardId = null;
            this.#pendingRestoreScroll = null;
          }
          return;
        }
      }

      if (typeof this.#pendingRestoreScroll === 'number') {
        window.scrollTo({ top: this.#pendingRestoreScroll, behavior: 'instant' });
        if (force) {
          this.#pendingRestoreCardId = null;
          this.#pendingRestoreScroll = null;
        }
      }
    } catch (err) {}
  }

  #storePageFragment(pageNumber, pageHTML) {
    try {
      if (!pageHTML || pageNumber < 2) return;

      const parsed = new DOMParser().parseFromString(pageHTML, 'text/html');
      const gridElement = parsed.querySelector('[ref="grid"]');
      if (!gridElement) return;
      
      const lis = Array.from(gridElement.querySelectorAll(':scope > [ref="cards[]"]'));
      if (!lis.length) return;

      const fragment = lis.map((li) => li.outerHTML).join('');
      sessionStorage.setItem(this.#storageKey + `:page:${pageNumber}`, fragment);

      const currentMax = Number(sessionStorage.getItem(this.#storageKey + ':maxPage') || '1');
      if (pageNumber > currentMax) sessionStorage.setItem(this.#storageKey + ':maxPage', String(pageNumber));
    } catch (err) {}
  }

  /* ----------------------------------------------------
     ORIGINAL INFINITE SCROLL LOGIC
  ---------------------------------------------------- */
  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    if (!viewMorePrevious && !viewMoreNext) return;

    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          if (this.#isRestoring) return;

          if (!this.#skipTransitionWait && viewTransition.current) {
            try {
              await viewTransition.current;
            } catch(e) {}
          }

          for (const entry of entries) {
            if (entry.isIntersecting) {
              const { viewMorePrevious, viewMoreNext } = this.refs;
              const loadBtn = this.refs.loadMoreButton || this.querySelector('.load-more-button');

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                if (this.getAttribute('infinite-scroll') === 'true') {
                  if (!this.#isAppending && loadBtn) {
                    loadBtn.click();
                  } else {
                    this.#renderNextPage();
                  }
                }
              }
            }
          }
        },
        { rootMargin: '100px' }
      );
    }

    if (viewMorePrevious) this.infinityScrollObserver.observe(viewMorePrevious);
    if (viewMoreNext) this.infinityScrollObserver.observe(viewMoreNext);
  }

  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const grid = this.refs.grid || this.querySelector('.product-grid');
    const lastPage = grid?.dataset?.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  async #fetchPage(type) {
    const page = this.#getPage(type);

    const resolvePromise = () => {
      if (type === 'next') {
        if (this.#resolveNextPagePromise) this.#resolveNextPagePromise();
        this.#resolveNextPagePromise = null;
      } else {
        if (this.#resolvePreviousPagePromise) this.#resolvePreviousPagePromise();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      resolvePromise();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch (e) {
      console.error("Fetch Error:", e);
    } finally {
      resolvePromise();
    }
  }

  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    
    if (pageContent) {
      this.pages.set(pageNumber, pageContent);
      this.#storePageFragment(pageNumber, pageContent);
    }
  }

  async #renderNextPage() {
    const grid = this.refs.grid || this.querySelector('.product-grid');
    if (!grid || this.#isAppending) return;

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    this.#isAppending = true;
    try {
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements || !nextPageItemElements.length) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        this.#fetchPage('next');
        await promise;

        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements || !nextPageItemElements.length) return;
      }

      grid.append(...nextPageItemElements);

      if (this.#aspectRatioHelper) {
        this.#aspectRatioHelper.processNewElements();
      }
      this.#updatePaginationText();

      requestIdleCallback(() => {
        this.#fetchPage('next');
      });

      try {
        const currentMax = Number(sessionStorage.getItem(this.#storageKey + ':maxPage') || '1');
        if (nextPage.page > currentMax) sessionStorage.setItem(this.#storageKey + ':maxPage', String(nextPage.page));
      } catch (err) {}
    } finally {
      this.#isAppending = false;
    }
  }

  async #renderPreviousPage() {
    const grid = this.refs.grid || this.querySelector('.product-grid');
    if (!grid || this.#isAppending) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    this.#isAppending = true;
    try {
      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      
      if (!previousPageItemElements || !previousPageItemElements.length) {
        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        this.#fetchPage('previous');
        await promise;
        
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements || !previousPageItemElements.length) return;
      }

      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      grid.prepend(...previousPageItemElements);

      if (this.#aspectRatioHelper) {
        this.#aspectRatioHelper.processNewElements();
      }
      this.#updatePaginationText();

      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      requestIdleCallback(() => {
        this.#fetchPage('previous');
      });
    } finally {
      this.#isAppending = false;
    }
  }

  #getPage(type) {
    const cards = this.refs.cards || Array.from(this.querySelectorAll('[ref="cards[]"]'));
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards) && !(cards instanceof NodeList)) return;
    if (cards.length === 0) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];
    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return Array.from(gridElement.querySelectorAll(':scope > [ref="cards[]"]'));
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  #handleFilterUpdate = () => {
    this.pages.clear();

    if (this.#resolveNextPagePromise) this.#resolveNextPagePromise();
    if (this.#resolvePreviousPagePromise) this.#resolvePreviousPagePromise();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;
    this.#isRestoring = false;

    try {
      const maxPage = Number(sessionStorage.getItem(this.#storageKey + ':maxPage') || '1');
      for (let p = 2; p <= maxPage; p++) {
        sessionStorage.removeItem(this.#storageKey + `:page:${p}`);
      }
    } catch (err) {}

    sessionStorage.removeItem(this.#storageKey + ':scroll');
    sessionStorage.removeItem(this.#storageKey + ':cardId');
    sessionStorage.removeItem(this.#storageKey + ':returning');
    sessionStorage.removeItem(this.#storageKey + ':maxPage');

    const grid = this.refs.grid || this.querySelector('.product-grid');
    const currentLastPage = grid?.dataset?.lastPage;

    const observer = new MutationObserver(() => {
      const newLastPage = grid?.dataset?.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        if (!this.isConnected) {
          return;
        }

        this.#observeViewMore();
        this.#updatePaginationText();
        this.#fetchPage('next');
      }
    });

    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true,
      });

      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 10000);
    }
  };

  /* ----------------------------------------------------
     OPTIONAL BUTTON LOGIC FOR CUSTOM PLP (STUCK-PROOF)
  ---------------------------------------------------- */
  #bindPaginationButtons() {
    const loadBtn = this.refs.loadMoreButton || this.querySelector('.load-more-button');
    const viewAllBtn = this.refs.viewAllButton || this.querySelector('[data-testid="plp-viewAll-select"]');

    if (loadBtn) {
      loadBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        if (loadBtn.getAttribute('data-disabled') === '1') return;

        this.#setButtonState(loadBtn, 'loading');
        try {
          await this.#renderNextPage();
          this.#setButtonState(loadBtn, 'success');
        } catch (err) {
        } finally {
          setTimeout(() => this.#setButtonState(loadBtn, 'idle'), 1000);
        }
      });
    }

    if (viewAllBtn) {
      viewAllBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        if (viewAllBtn.getAttribute('data-disabled') === '1') return;

        this.#setButtonState(viewAllBtn, 'loading');
        try {
          let hasMore = true;
          while (hasMore) {
            const nextPage = this.#getPage('next');
            if (!nextPage || !this.#shouldUsePage(nextPage)) {
              hasMore = false;
              break;
            }
            await this.#renderNextPage();
          }
          this.#setButtonState(viewAllBtn, 'success');
        } catch (err) {
        } finally {
          setTimeout(() => this.#setButtonState(viewAllBtn, 'idle'), 1000);
        }
      });
    }
  }

  #resetAllButtonStates() {
    const loadBtn = this.refs.loadMoreButton || this.querySelector('.load-more-button');
    const viewAllBtn = this.refs.viewAllButton || this.querySelector('[data-testid="plp-viewAll-select"]');
    this.#setButtonState(loadBtn, 'idle');
    this.#setButtonState(viewAllBtn, 'idle');
  }

  #setButtonState(btn, state) {
    if (!btn) return;
    btn.classList.remove('is-loading', 'is-success');

    if (state === 'loading') {
      btn.setAttribute('disabled', 'disabled');
      btn.setAttribute('data-disabled', '1');
      btn.classList.add('is-loading');
    } else if (state === 'success') {
      btn.removeAttribute('disabled');
      btn.setAttribute('data-disabled', '0');
      btn.classList.add('is-success');
    } else {
      btn.removeAttribute('disabled');
      btn.setAttribute('data-disabled', '0');
    }
  }

  #updatePaginationText() {
    const paginationText = this.refs.paginationText || this.querySelector('.pagination-details');
    const buttonsContainer = this.refs.buttonsContainer || this.querySelector('.custom-plp-buttons');
    const grid = this.refs.grid || this.querySelector('.product-grid');

    if (!grid || !paginationText) return;

    const total = Number(grid.dataset.totalCount || 0);
    const loadedCards = Array.from(this.querySelectorAll('[ref="cards[]"]'));
    const loaded = loadedCards.length;

    if (total === 0 || loaded === 0) return;

    const from = 1;
    const to = Math.min(total, loaded);

    paginationText.textContent = `Viewing ${from} - ${to} of ${total} products`;

    if (buttonsContainer) {
      const lastPageLoaded = parseInt(loadedCards[loadedCards.length - 1]?.dataset?.page || 1, 10);
      const lastPageAvailable = Number(grid.dataset.lastPage || 1);
      buttonsContainer.style.display = lastPageLoaded >= lastPageAvailable ? 'none' : 'flex';
    }
  }
}